import { Text } from "@chakra-ui/react";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  GetDashboardFormSchoolGradeRankResponse,
  GetGradeConfigByEnrollmentPeriodResponse,
  GetSchoolsByEnrollmentPeriodResponse,
} from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import * as Format from "src/services/format";
import { isNonEmptyString } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { ChartTooltip } from "./ChartTooltip";
import { DashboardChart } from "./DashboardChart";
import { Grade, GradesMultiSelectInput } from "./GradesMultiSelectInput";
import { School, SchoolsMultiSelectInput } from "./SchoolsMultiSelectInput";

type Props = {
  exportId?: string;
  remoteData: [
    RD.RemoteData<unknown, GetDashboardFormSchoolGradeRankResponse>,
    RD.RemoteData<unknown, GetSchoolsByEnrollmentPeriodResponse>,
    RD.RemoteData<unknown, GetGradeConfigByEnrollmentPeriodResponse>
  ];
};
const ASPECT_RATIO = 1.5;
export const ResponsesByRank: React.FC<Props> = ({
  exportId,
  remoteData: [formRD, schoolsRD, gradesRD],
}) => {
  const [selectedSchools, setSelectedSchools] = React.useState<School[] | null>(
    null
  );
  const [selectedGrades, setSelectedGrades] = React.useState<Grade[] | null>(
    null
  );

  const allSchools = React.useMemo(() => {
    if (schoolsRD.hasData()) {
      return schoolsRD.data.data;
    }
    return [];
  }, [schoolsRD]);

  const allGrades = React.useMemo(() => {
    if (gradesRD.hasData()) {
      return gradesRD.data.data;
    }
    return [];
  }, [gradesRD]);

  const data = React.useMemo(() => {
    return formRD.map((formData) => {
      return transformData(formData, selectedSchools, selectedGrades);
    });
  }, [formRD, selectedSchools, selectedGrades]);

  const { glossary } = useGlossary();

  return (
    <DashboardChart
      aspectRatio={ASPECT_RATIO}
      title="Responses by rank"
      caption={glossary`Form responses by rank, not unique by student`}
      data={data}
      exportId={exportId}
      exportFileName="responses-by-rank"
      filters={
        <>
          <SchoolsMultiSelectInput
            remoteData={schoolsRD}
            onChange={setSelectedSchools}
            selectedSchools={selectedSchools ?? allSchools}
          />
          <GradesMultiSelectInput
            remoteData={gradesRD}
            onChange={setSelectedGrades}
            selectedGrades={selectedGrades ?? allGrades}
          />
        </>
      }
    >
      {(data) => (
        <BarChart
          style={{
            width: "100%",
            aspectRatio: ASPECT_RATIO,
          }}
          responsive
          layout="vertical"
          data={data}
          margin={{
            top: 0,
            right: 24,
            left: 0,
            bottom: 5,
          }}
        >
          <defs>
            <linearGradient id="rankBarFill" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#7A57D1" />
              <stop offset="100%" stopColor="#845CD9" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical />
          <XAxis
            type="number"
            orientation="top"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            tickLine={false}
            axisLine={true}
            fontSize="0.75rem"
            interval={0}
          />
          <Tooltip<number, string>
            content={({ active, payload, label }) => {
              const value = payload[0]?.payload?.value;
              if (active && payload && value) {
                return (
                  <ChartTooltip accentColor="#845CD9" title={`Rank ${label}`}>
                    <Text fontSize="0.9rem">
                      <strong>{Format.formatWholeNumber(value)}</strong>{" "}
                      {value === 1 ? "response" : "responses"}
                    </Text>
                  </ChartTooltip>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="value" fill="url(#rankBarFill)" barSize={30}>
            <LabelList dataKey="value" position="right" fill="#845CD9" />
          </Bar>
        </BarChart>
      )}
    </DashboardChart>
  );
};

export function transformData(
  formData: GetDashboardFormSchoolGradeRankResponse,
  selectedSchools: School[] | null,
  selectedGrades: Grade[] | null
): Array<{ name: string; value: number }> {
  const rankTotals = new Map<number, number>();

  for (const row of formData.data) {
    // null means "all selected" — skip that filter entirely
    // empty array means no schools selected — only include rows without a school
    if (selectedSchools !== null) {
      if (isNonEmptyString(row.school_id)) {
        if (selectedSchools.length === 0) {
          continue;
        }
        if (!selectedSchools.find((s) => s.id === row.school_id)) {
          continue;
        }
      } else {
        if (selectedSchools.length > 0) {
          continue;
        }
      }
    }

    if (selectedGrades !== null) {
      if (isNonEmptyString(row.grade_config_id)) {
        if (selectedGrades.length === 0) {
          continue;
        }
        if (!selectedGrades.find((g) => g.id === row.grade_config_id)) {
          continue;
        }
      } else {
        if (selectedGrades.length > 0) {
          continue;
        }
      }
    }

    let count = 0;
    if (row === undefined) continue;
    if (row.count === null) continue;
    if (typeof row.count === "number") {
      count = row.count;
    } else {
      count = parseInt(row.count);
    }

    if (row.rank === null) continue;
    const currentTotal = rankTotals.get(row.rank) ?? 0;
    rankTotals.set(row.rank, currentTotal + count);
  }

  const uniqueRanks = Array.from(rankTotals.keys()).sort((a, b) => a - b);
  return uniqueRanks.map((rank) => ({
    name: String(rank + 1),
    value: rankTotals.get(rank) ?? 0,
  }));
}
